import React from 'react';
import { useSourcesEntryScreen } from './hooks/useSourcesEntryScreen';

export default function SourcesEntryScreen() {
  const { state, actions } = useSourcesEntryScreen();

  return (
    <div className="mx-auto flex w-full max-w-[1100px] flex-col gap-6 pb-16">
      {/* Header Banner */}
      <section className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between border-b border-outline-variant/10 pb-5">
        <div>
          <div className="flex items-center gap-2 text-xs font-semibold text-on-surface-variant mb-1">
            <span>Masters</span>
            <span className="material-symbols-outlined text-[12px]">chevron_right</span>
            <span>Admissions & Marketing</span>
            <span className="material-symbols-outlined text-[12px]">chevron_right</span>
            <button
              type="button"
              onClick={actions.handleCancel}
              className="text-on-surface-variant hover:text-primary transition cursor-pointer"
            >
              Source Master
            </button>
            <span className="material-symbols-outlined text-[12px]">chevron_right</span>
            <span className="text-primary font-bold">
              {state.isEditMode ? `Edit Source #${state.id}` : 'New Source'}
            </span>
          </div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight text-on-surface">
            {state.isEditMode ? 'Edit Inquiry Source' : 'Create Inquiry Source'}
          </h1>
          <p className="mt-1 text-xs text-on-surface-variant font-medium">
            Define marketing channels and inquiry referral origins for student admissions and CRM tracking.
          </p>
        </div>

        {/* Action Buttons Top */}
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={actions.handleCancel}
            disabled={state.isSaving}
            className="inline-flex items-center justify-center rounded-xl border border-outline-variant/40 bg-surface px-4 py-2 text-xs font-bold text-on-surface hover:bg-surface-container-high transition cursor-pointer disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={actions.handleSave}
            disabled={state.isSaving || state.isLoading}
            className="inline-flex items-center gap-2 rounded-xl bg-primary px-5 py-2 text-xs font-bold text-on-primary shadow-sm hover:brightness-105 active:scale-[0.98] transition cursor-pointer disabled:opacity-50"
          >
            {state.isSaving && (
              <span className="material-symbols-outlined animate-spin text-sm">
                progress_activity
              </span>
            )}
            <span>{state.isEditMode ? 'Update Source' : 'Save Source'}</span>
          </button>
        </div>
      </section>

      {/* Main Grid Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
        {/* Form Card */}
        <div className="lg:col-span-2 rounded-2xl border border-outline-variant/20 bg-surface-container-lowest p-6 shadow-xs flex flex-col gap-6">
          <h2 className="text-sm font-bold text-on-surface flex items-center gap-2 border-b border-outline-variant/15 pb-3">
            <span className="material-symbols-outlined text-primary text-lg">campaign</span>
            <span>Source Definition</span>
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {/* Source Name */}
            <div className="md:col-span-2 flex flex-col gap-1.5">
              <label className="text-xs font-bold text-on-surface">
                Source Name <span className="text-error">*</span>
              </label>
              <input
                type="text"
                value={state.form.name || ''}
                onChange={(e) => actions.patchForm({ name: e.target.value })}
                placeholder="e.g. Social Media, Google Search, Education Fair, Referral"
                maxLength={100}
                required
                className="w-full rounded-xl border border-outline-variant/30 bg-surface-container-lowest px-4 py-2.5 text-xs text-on-surface outline-none transition focus:border-primary focus:ring-1 focus:ring-primary/20 shadow-2xs"
              />
              <span className="text-[10px] text-on-surface-variant font-medium">
                Descriptive name for the marketing or lead acquisition channel.
              </span>
            </div>

            {/* Short Name / Abbreviation */}
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-bold text-on-surface">
                Abbreviation / Short Code
              </label>
              <input
                type="text"
                value={state.form.short_name || ''}
                onChange={(e) => actions.patchForm({ short_name: e.target.value })}
                placeholder="e.g. SOC, GOOG, REF, FAIR"
                maxLength={20}
                className="w-full rounded-xl border border-outline-variant/30 bg-surface-container-lowest px-4 py-2.5 text-xs text-on-surface outline-none transition focus:border-primary focus:ring-1 focus:ring-primary/20 shadow-2xs uppercase"
              />
              <span className="text-[10px] text-on-surface-variant font-medium">
                Standard abbreviation used on call logs, admission cards, and compact tables.
              </span>
            </div>

            {/* Operational Status */}
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-bold text-on-surface">
                Operational Status
              </label>
              <div className="flex items-center gap-1.5 p-1 rounded-xl bg-surface-container-high w-fit shadow-xs">
                <button
                  type="button"
                  onClick={() => actions.setOperationalStatus('active')}
                  className={`px-4 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer select-none ${
                    state.operationalStatus === 'active'
                      ? 'bg-primary text-on-primary shadow-xs'
                      : 'text-on-surface-variant hover:text-on-surface'
                  }`}
                >
                  Active
                </button>
                <button
                  type="button"
                  onClick={() => actions.setOperationalStatus('inactive')}
                  className={`px-4 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer select-none ${
                    state.operationalStatus === 'inactive'
                      ? 'bg-primary text-on-primary shadow-xs'
                      : 'text-on-surface-variant hover:text-on-surface'
                  }`}
                >
                  Inactive
                </button>
              </div>
              <span className="text-[10px] text-on-surface-variant font-medium">
                Archived sources are hidden from new student admission and inquiry forms.
              </span>
            </div>
          </div>

          {/* Form Actions Footer */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-outline-variant/15">
            <button
              type="button"
              onClick={actions.handleCancel}
              disabled={state.isSaving}
              className="rounded-xl border border-outline-variant/40 bg-surface px-4 py-2 text-xs font-bold text-on-surface hover:bg-surface-container-high transition cursor-pointer"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={actions.handleSave}
              disabled={state.isSaving || state.isLoading}
              className="inline-flex items-center gap-2 rounded-xl bg-primary px-5 py-2 text-xs font-bold text-on-primary shadow-sm hover:brightness-105 active:scale-[0.98] transition cursor-pointer disabled:opacity-50"
            >
              {state.isSaving && (
                <span className="material-symbols-outlined animate-spin text-sm">
                  progress_activity
                </span>
              )}
              <span>{state.isEditMode ? 'Update Source' : 'Save Source'}</span>
            </button>
          </div>
        </div>

        {/* Sidebar Overview Card */}
        <div className="flex flex-col gap-5">
          <div className="rounded-2xl border border-outline-variant/20 bg-surface-container-lowest p-5 shadow-xs flex flex-col gap-4">
            <h3 className="text-xs font-bold uppercase tracking-wider text-on-surface-variant flex items-center gap-1.5">
              <span className="material-symbols-outlined text-base text-primary">info</span>
              <span>Attribution Guidance</span>
            </h3>

            <div className="text-xs text-on-surface-variant space-y-2.5">
              <p>
                <strong>Multi-Channel Tracking</strong>: Inquiry sources categorize prospective leads across digital campaigns, outdoor media, and institutional partnerships.
              </p>
              <p>
                <strong>Analytics & Funnel ROI</strong>: Counselor and admission managers rely on source tags to measure cost-per-lead and conversion efficiency.
              </p>
            </div>

            <div className="rounded-xl bg-surface-container p-3 text-[11px] text-on-surface-variant flex flex-col gap-1 border border-outline-variant/15">
              <span className="font-bold text-on-surface">Data Integrity:</span>
              <span>Source names must be unique. Deleting a source softly archives it so historical inquiry and admission attribution records remain intact.</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
